// Manual clean removal — identity, rule classification and row patching.
//
// A clean is NOT a row. One physical CIP is stamped onto every output row of
// the batch it belongs to: all the line products sharing a kitchen batch carry
// the same `kitchen_clean_start_time`, which is why both timeline builders in
// `FactoryScheduleView.view.tsx` dedup their bars on `asset|start|end|kind`
// before drawing. So the unit of removal is `(side, resource, kind, start,
// end)` and stripping it has to patch EVERY row that carries it — a row-scoped
// edit would delete one copy and leave the clean on screen via its siblings.
//
// A clean also lives in two representations at once. `postprocess.rs` writes
// every CIP event into `additional_{kitchen,line}_cips` as
// `[{cip, cip_id, start, end, reason}, ...]`, and separately "promotes" one of
// them into the scalar `*_clean_*` / `*_preclean_*` columns. Removing a clean
// therefore means clearing the scalar pair AND pruning the matching entry out
// of the JSON array; doing only one leaves the clean visible in whichever
// views read the other (the output route's `GenerateCleaningRows` prefers the
// JSON array, the timeline builders read both).

import { InputRow } from './SchedulerView.types'

// ─── Wire format ─────────────────────────────────────────────────────────────

/** Entry in `additional_kitchen_cips` / `additional_line_cips`, exactly as
 *  `postprocess.rs::cip_events_to_json` emits it. */
export type AdditionalCipEvent = {
  start: number
  end: number
  cip?: string
  cip_id?: string
  reason?: string
}

/** Malformed JSON yields `[]` rather than throwing — a row with a broken
 *  array must not take a whole view down with it. */
export const parseAdditionalCips = (
  raw: string | null | undefined
): AdditionalCipEvent[] => {
  if (!raw) return []
  try {
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

/** The `reason` recorded for the CIP occupying `[start, end)`, or `undefined`
 *  when the row carries only the promoted scalar copy and no JSON array. */
export const cipReasonFor = (
  raw: string | null | undefined,
  start: number | null | undefined,
  end: number | null | undefined
): string | undefined => {
  if (start == null || end == null) return undefined
  return parseAdditionalCips(raw).find(
    (e) => e.start === start && e.end === end
  )?.reason
}

// ─── Clean identity ──────────────────────────────────────────────────────────

export type CleanSide = 'kitchen' | 'line'
/** `clean` is the post-clean (`*_clean_*`), `preclean` the pre-clean
 *  (`*_preclean_*`) — the same split the scalar columns use. A max-run clean
 *  occupies the preclean columns; it is told apart by `reason`, not by kind. */
export type CleanKind = 'clean' | 'preclean'

/** One physical clean, addressed the way the timeline dedups it. */
export type CleanRef = {
  side: CleanSide
  /** Kitchen or line name — whichever `side` says. */
  resource: string
  kind: CleanKind
  start: number
  end: number
}

/** Stable string form for React state (a `Set<string>` of pending removals)
 *  and for cache keys. Times go in raw: they come straight off the same JSON
 *  the comparison below uses, so they round-trip exactly. */
export const cleanRefKey = (ref: CleanRef): string =>
  `${ref.side}|${ref.resource}|${ref.kind}|${ref.start}|${ref.end}`

const SCALAR_FIELDS: Record<
  CleanSide,
  Record<CleanKind, { start: string; end: string }>
> = {
  kitchen: {
    clean: {
      start: 'kitchen_clean_start_time',
      end: 'kitchen_clean_end_time',
    },
    preclean: {
      start: 'kitchen_preclean_start_time',
      end: 'kitchen_preclean_end_time',
    },
  },
  line: {
    clean: { start: 'line_clean_start_time', end: 'line_clean_end_time' },
    preclean: {
      start: 'line_preclean_start_time',
      end: 'line_preclean_end_time',
    },
  },
}

const ARRAY_FIELD: Record<CleanSide, string> = {
  kitchen: 'additional_kitchen_cips',
  line: 'additional_line_cips',
}

/** The row column naming the resource this clean ran on. */
const RESOURCE_FIELD: Record<CleanSide, string> = {
  kitchen: 'kitchen',
  line: 'line',
}

// ─── Rule classification ─────────────────────────────────────────────────────

/**
 * Whether removing this clean contradicts the rule that created it.
 *
 * `discretionary` cleans are the two the engine itself treats as structurally
 * flexible (`cip_compaction.rs` moves end-of-use post-cleans freely and leaves
 * rule-bound ones alone). `mandated` cleans exist because a rule table said
 * so, and removing one produces a schedule the engine would never emit — the
 * planner is allowed to, but is told what they are overriding.
 *
 * `unknown` is a real case, not a fallback for tidiness: a row carrying only
 * the promoted scalar copy has no `reason` recorded anywhere, so we cannot
 * claim the removal is safe.
 */
export type CleanRemovalRisk =
  | { kind: 'discretionary'; reason: string; note: string }
  | { kind: 'mandated'; reason: string; rule: string; note: string }
  | { kind: 'unknown' }

export const cleanRemovalRisk = (
  reason: string | undefined
): CleanRemovalRisk => {
  switch (reason) {
    case 'end_of_use':
      return {
        kind: 'discretionary',
        reason,
        note: 'Nothing is scheduled on this unit after it. Removing it shortens the visible end of the schedule without moving any production.',
      }
    case 'idle_preclean':
      return {
        kind: 'discretionary',
        reason,
        note: 'Placed opportunistically while the unit was idle. Removing it may mean a required clean has to happen later instead.',
      }
    case 'material_change':
      return {
        kind: 'mandated',
        reason,
        rule: 'Base Material Changeover',
        note: 'Required between these two base materials. Removing it runs the next material straight after the previous one with no clean in between.',
      }
    case 'max_run_hours':
      return {
        kind: 'mandated',
        reason,
        rule: 'Max run hours',
        note: 'Required because the unit reached its continuous-run limit. Removing it lets the run continue past that limit.',
      }
    default:
      return { kind: 'unknown' }
  }
}

// ─── Locating and stripping ──────────────────────────────────────────────────

const num = (v: unknown): number | null =>
  typeof v === 'number' && Number.isFinite(v) ? v : null

const rowResource = (row: InputRow, side: CleanSide): string =>
  String(
    (row as unknown as Record<string, unknown>)[RESOURCE_FIELD[side]] ?? ''
  )

/**
 * Every scalar pair on this side whose window is exactly this clean.
 *
 * Deliberately checks BOTH `clean` and `preclean` rather than trusting
 * `ref.kind`. An event taken from `additional_*_cips` does not say which pair
 * it was promoted into, and a max-run clean sits in the preclean columns while
 * reading as a clean — so a `kind` mismatch would silently leave the scalar
 * copy behind and the clean would still render. A unit cannot be cleaned twice
 * over the same hours, so matching on the window alone is unambiguous.
 */
const matchingScalarPairs = (
  row: InputRow,
  ref: CleanRef
): { start: string; end: string }[] => {
  const rec = row as unknown as Record<string, unknown>
  const kinds: CleanKind[] = ['clean', 'preclean']
  return kinds
    .map((kind) => SCALAR_FIELDS[ref.side][kind])
    .filter(
      (f) => num(rec[f.start]) === ref.start && num(rec[f.end]) === ref.end
    )
}

/** True if any scalar pair on this side is exactly this clean. */
const scalarMatches = (row: InputRow, ref: CleanRef): boolean =>
  matchingScalarPairs(row, ref).length > 0

/** True if `row`'s JSON array carries an event for exactly this window. */
const arrayMatches = (row: InputRow, ref: CleanRef): boolean => {
  const rec = row as unknown as Record<string, unknown>
  return parseAdditionalCips(rec[ARRAY_FIELD[ref.side]] as string).some(
    (e) => e.start === ref.start && e.end === ref.end
  )
}

/**
 * Every row carrying this clean, in either representation, on the matching
 * resource. The resource check matters: two different kitchens can clean over
 * the same hours, and their scalar columns are indistinguishable without it.
 */
export const findCleanRows = (rows: InputRow[], ref: CleanRef): InputRow[] =>
  rows.filter(
    (row) =>
      rowResource(row, ref.side) === ref.resource &&
      (scalarMatches(row, ref) || arrayMatches(row, ref))
  )

/** The recorded `reason` for a clean, taken from the first row that has it. */
export const cleanReason = (
  rows: InputRow[],
  ref: CleanRef
): string | undefined => {
  for (const row of findCleanRows(rows, ref)) {
    const rec = row as unknown as Record<string, unknown>
    const found = parseAdditionalCips(
      rec[ARRAY_FIELD[ref.side]] as string
    ).find((e) => e.start === ref.start && e.end === ref.end)
    if (found?.reason) return found.reason
  }
  return undefined
}

/**
 * Resolve a clean identity taken from a RENDERED bar back to the clean as the
 * stored rows hold it.
 *
 * Bars are drawn from week-rebased rows, so their times are display hours —
 * and a clean crossing a week boundary has been clamped to `[0, WEEK_HOURS]`,
 * which means adding `weekBaseHour` back does NOT recover the stored window.
 * The write has to match the stored rows, so resolve against them: shift the
 * displayed window into absolute hours and take the clean on the same
 * side/resource occupying it. An exact match wins; otherwise the single
 * overlapping clean, which is unambiguous because a unit cannot be cleaned
 * twice over the same hours.
 *
 * Returns `null` when nothing matches, or when more than one distinct window
 * overlaps — better to refuse than to strip the wrong clean.
 */
export const resolveCleanRef = (
  absoluteRows: InputRow[],
  displayed: CleanRef,
  weekBaseHour: number | null
): CleanRef | null => {
  const base = weekBaseHour ?? 0
  const lo = displayed.start + base
  const hi = displayed.end + base
  const kinds: CleanKind[] = ['clean', 'preclean']
  const candidates: CleanRef[] = []

  for (const row of absoluteRows) {
    if (rowResource(row, displayed.side) !== displayed.resource) continue
    const rec = row as unknown as Record<string, unknown>
    for (const kind of kinds) {
      const f = SCALAR_FIELDS[displayed.side][kind]
      const s = num(rec[f.start])
      const e = num(rec[f.end])
      if (s !== null && e !== null && e > s) {
        candidates.push({ ...displayed, kind, start: s, end: e })
      }
    }
    for (const ev of parseAdditionalCips(
      rec[ARRAY_FIELD[displayed.side]] as string
    )) {
      if (
        typeof ev.start === 'number' &&
        typeof ev.end === 'number' &&
        ev.end > ev.start
      ) {
        candidates.push({ ...displayed, start: ev.start, end: ev.end })
      }
    }
  }

  const exact = candidates.find((c) => c.start === lo && c.end === hi)
  if (exact) return exact

  // The same clean is stamped on every row of its batch, so dedup by window
  // before deciding whether the overlap is unique.
  const windows = new Map<string, CleanRef>()
  for (const c of candidates) {
    if (c.end > lo && c.start < hi) windows.set(`${c.start}|${c.end}`, c)
  }
  return windows.size === 1 ? [...windows.values()][0] : null
}

/** Column values to write for one row. `null` clears a scalar time. */
export type RowPatch = Record<string, string | number | null>

/**
 * Patches that remove `ref` from `rows`, keyed by row `id`.
 *
 * Each patch carries only the columns that change, plus `id` so the Delta
 * merge on `source.id = target.id` can find its target. Scalar times are set
 * to `null`; the JSON array is re-serialised without the matching event —
 * `'[]'` rather than `''` when nothing is left, because
 * `DeltaTableService.update_delta_table` maps empty strings to null and then
 * has to guess a type back for an all-null column.
 *
 * Returns `{}` when no row carries the clean, so a stale click (the schedule
 * was re-run underneath) writes nothing rather than blanking the wrong rows.
 */
export const stripCleanFromRows = (
  rows: InputRow[],
  ref: CleanRef
): Record<string, RowPatch> => {
  const patches: Record<string, RowPatch> = {}
  for (const row of findCleanRows(rows, ref)) {
    const rec = row as unknown as Record<string, unknown>
    const id = rec.id
    // Without an id the merge has nothing to match on — skip rather than
    // write a row that would be appended as a duplicate.
    if (typeof id !== 'string' || id === '') continue

    const patch: RowPatch = { id }

    for (const f of matchingScalarPairs(row, ref)) {
      patch[f.start] = null
      patch[f.end] = null
    }

    const arrayField = ARRAY_FIELD[ref.side]
    const events = parseAdditionalCips(rec[arrayField] as string)
    const kept = events.filter(
      (e) => !(e.start === ref.start && e.end === ref.end)
    )
    if (kept.length !== events.length) {
      patch[arrayField] = JSON.stringify(kept)
    }

    // Matched on the resource but neither representation actually changed —
    // nothing to write for this row.
    if (Object.keys(patch).length > 1) patches[id] = patch
  }
  return patches
}

/**
 * Apply patches to a row set in memory, for an optimistic view update while
 * the write is in flight. Rows are copied, never mutated.
 */
export const applyPatches = (
  rows: InputRow[],
  patches: Record<string, RowPatch>
): InputRow[] => {
  if (Object.keys(patches).length === 0) return rows
  return rows.map((row) => {
    const rec = row as unknown as Record<string, unknown>
    const patch = typeof rec.id === 'string' ? patches[rec.id] : undefined
    return patch ? ({ ...rec, ...patch } as unknown as InputRow) : row
  })
}
